import { AnnotationIconColorType, AnnotationIconType } from '@/api/storymapCardDetails/queries';
import { PersonaPicture } from '@/pages/main/managePersonas/types';
import { IRealTimeStoreState } from '@/_core/fluxstore/types';
import {
    BasicPrioValue,
    BusinessValue,
    Effort,
    KanoPrioValue,
    MoscowPrioValue,
    StorymapPrioFrameworkType,
    StorymapTheme,
} from '../../api/queries';
import { CardColor } from '@/api/workspaceSettings/queries';
import { StorymapCollaborator } from '@/store/main/types';
import cloneDeep from 'lodash/cloneDeep';
import { CardType } from '../use';
import { IssueTypeAbbreviation } from '../../utils/issueTypeAbbreviations';
import { PublishedStorymapAccessKind } from '../../utils/publishedStorymapRoutes';

export class StorymapState implements IRealTimeStoreState {
    id: string = '';
    name: string = '';
    slug: string = '';
    productSlug: string = '';
    estimationUnit: string = '';
    aboutBoardDescription: string = '';
    isTemplate: boolean = false;
    theme: StorymapTheme = StorymapTheme.Light;
    activityColor: CardColor = CardColor.White;
    taskColor: CardColor = CardColor.White;
    subtaskColor: CardColor = CardColor.White;
    cardKeyPrefix: string = '';
    cardKeyCounter: number = 0;
    isCardKeyEnabled: boolean = false;
    aiData: StorymapAiData = null;
    customAiStoryTemplates: CustomAiStoryTemplate[] = [];
    customAiStoryTemplateExamples: CustomAiStoryTemplate[] = [];
    isSampleStorymap: boolean = false;
    aiTemplateOverrides: StorymapAiTemplateOverride[] = [];
    releases: Release[] = [];
    activities: Activity[] = [];
    tasks: Task[] = [];
    subtasks: Subtask[] = [];
    subtaskStatuses: SubtaskStatus[] = [];

    colors: Color[] = [];
    personas: Persona[] = [];
    annotations: Annotation[] = [];
    personaPictures: PersonaPicture[] = [];
    collaborators: StorymapCollaborator[] = [];

    defaultPersonaCustomFields: PersonaCustomField[] = [];
    defaultPersonaPersonalities: PersonaPersonality[] = [];

    canEdit: boolean = false;
    canAdminister: boolean = false;
    canCopyPrivateLink: boolean = false;
    canCopyPrivateReleaseLink: boolean = false;
    isNextGenPrivateLinkSharingEnabled: boolean = false;
    hasFeedbackPortalAccess: boolean = false;

    hasAlreadyPinnedRelease: boolean = false;

    onlineUsers: string[] = [];

    currentViewType: ViewType = ViewType.Discovery;
    discoveryViewOptions: ViewTypeOptions = { ...defaultViewOptions };
    planningViewOptions: ViewTypeOptions = { ...defaultViewOptions };
    backlogViewOptions: ViewTypeOptions = { ...defaultViewOptions };
    trackingViewOptions: ViewTypeOptions = {
        isShowAnnotation: true,
        isShowIssueType: false,
        isShowAssignee: false,
        isShowPersonaBlock: false,
        isShowPrioritization: false,
    };

    prioFrameworkType: StorymapPrioFrameworkType = StorymapPrioFrameworkType.Basic;

    isInitialized: boolean = false;
    isPublishedRecipient: boolean = false;
    publishedShowUserIdentities: boolean = false;
    publishedShowEstimation: boolean = false;
    showAboutBoard: boolean = true;
    showReleaseDetails: boolean = true;
    publishedAccessKind: PublishedStorymapAccessKind | null = null;
    publishedAccessKey: string = '';
    canComment: boolean = false;
    isAiStorymapLoading: boolean = false;
    aiCreateStoryMapErrorMessage: string = '';
    correlationId: string = '';
    storeType: string = 'storymap';

    isReleaseManagementOpen: boolean = false;
    isThemeSettingsDialogOpen: boolean = false;

    cardDetailsDialogState: CardDetailsDialogState = {
        callbackAfterClose: null,
    };

    personaManagementPopupState: PersonaManagementPopupState = {
        isOpen: false,
        startGeneratingWithAi: false,
        onAiDialogClose: null,
        onPersonaManagementClose: null,
    };

    collectUserGoalsDialogState: CollectUserGoalsDialogState = {
        isOpen: false,
        activityOrder: null,
        scope: null,
    };

    zoomLevel: 1 | 2 | 3 = 2;

    justDraged: boolean = false;
    isDraggingCard: boolean = false;
    dragState: {
        cardBeingDragged: Activity | Task | Subtask;
        isCardDraggedOut: boolean;
        hasJustBeenDraggedOut: boolean;
        targetParentCard: Activity | Task | Subtask;
        newIndex: number;
    } = {
        cardBeingDragged: null,
        isCardDraggedOut: false,
        hasJustBeenDraggedOut: false,
        targetParentCard: null,
        newIndex: null,
    };

    isIntegrationEnabled: boolean = false;
    isImportEnabled: boolean = false;
    integrationToolType: ToolIntegrationToolType = ToolIntegrationToolType.NotIntegrated;
    isWsLevelIntegrationOauth1: boolean = false;
    isDisabledWsLevelIntegration: boolean = false;
    isCorporateWsLevelIntegration: boolean = false;
    isMissingImpersonationToken: boolean = false;
    canPushTaskLevel: boolean = false;
    canPushActivityLevel: boolean = false;
    hasImportableCards: boolean = false;
    issueTypeAbbreviations: IssueTypeAbbreviation[] = [];

    shouldShowOnboarding: boolean = false;
    isWorkspaceVisible: boolean = false;
    viewersCanCommentOnCards: boolean = false;
    canWorkspaceMembersComment: boolean = false;
    linkedUrl: string = '';
    shouldOpenIntegrationTokenExpiredDialog: boolean = false;

    aiDataDialogState: AiDataDialogState = {
        isAiDataDialogOpen: false,
        onDialogClose: null,
    };

    filterState: FilterState = cloneDeep(defaultFilters);

    trackingViewState: TrackingViewState = {
        releaseId: null,
        filterTaskIds: [],
        filterActivityIds: [],
        hoveredCardId: null,
    };
    trashState: TrashState = {
        rows: [],
        selectedRowId: null,
        isLoading: false,
        loadError: '',
        isRowOperationInProgress: false,
        isEmptying: false,
        trashLoadToken: 0,
        rowOperationToken: 0,
        emptyOperationToken: 0,
        isDialogOpen: false,
    };
    navigationState: NavigationState = {
        originalSubtask: null,
        shouldKeepOriginalCardRectOnSelectionChange: false,
        activeCardsByOtherUsers: {},
    };
}

export interface TrashState {
    rows: TrashRow[];
    selectedRowId: string | null;
    isLoading: boolean;
    loadError: string;
    isRowOperationInProgress: boolean;
    isEmptying: boolean;
    trashLoadToken: number;
    rowOperationToken: number;
    emptyOperationToken: number;
    isDialogOpen: boolean;
}

export interface TrashRow {
    id: string;
    cardType: number;
    title: string;
    trashedAt: string;
    trashedByUserName: string;
    originalActivityId?: string;
    originalActivityTitle?: string;
    originalTaskId?: string;
    originalTaskTitle?: string;
    originalReleaseId?: string;
    originalReleaseName?: string;
    isUnscheduled: boolean;
    childTaskCount: number;
    childSubtaskCount: number;
    hasUnavailableActivityWarning: boolean;
    hasUnavailableTaskWarning: boolean;
    hasUnavailableReleaseWarning: boolean;
    hasArchivedReleaseWarning: boolean;
}

interface NavigationState {
    originalSubtask: Subtask | null;
    shouldKeepOriginalCardRectOnSelectionChange: boolean;
    activeCardsByOtherUsers: Record<string, Activity | Task | Subtask>;
}

interface CheckboxStates {
    createdByOthers: boolean;
    createdByMe: boolean;
    lastUpdatedByOthers: boolean;
    lastUpdatedByMe: boolean;
    hasDetails: boolean;
    hasNoDetails: boolean;
    hasEstimation: boolean;
    hasNoEstimation: boolean;
    hasAttachment: boolean;
    hasNoAttachment: boolean;
    isLinkedToIssueTracker: boolean;
    isNotLinkedToIssueTracker: boolean;
}

export interface FilterState {
    assigneeUserIds: string[];
    statusKeys: number[];
    personaIds: string[];
    annotationIds: string[];
    prioIds: number[];
    businessValues: BusinessValue[];
    efforts: Effort[];
    colors: CardColor[];
    updatedDateFrom: Date;
    updatedDateTo: Date;

    checkboxStates: CheckboxStates;

    hideUnfiltered: boolean;
    newlyCreatedCardIds: string[];
}

export const defaultFilters: FilterState = {
    assigneeUserIds: [],
    statusKeys: [],
    personaIds: [],
    annotationIds: [],
    prioIds: [],
    businessValues: [],
    efforts: [],
    colors: [],
    updatedDateFrom: null,
    updatedDateTo: null,
    checkboxStates: {
        createdByMe: false,
        createdByOthers: false,
        hasAttachment: false,
        hasDetails: false,
        hasEstimation: false,
        hasNoAttachment: false,
        hasNoDetails: false,
        hasNoEstimation: false,
        isLinkedToIssueTracker: false,
        isNotLinkedToIssueTracker: false,
        lastUpdatedByMe: false,
        lastUpdatedByOthers: false,
    },
    hideUnfiltered: false,
    newlyCreatedCardIds: [],
};

export interface TrackingViewState {
    releaseId: string | null;
    filterTaskIds: string[];
    filterActivityIds: string[];
    hoveredCardId: string;
}

export interface StorymapAiData {
    targetUsers: string;
    typeOfProduct: string;
    purpose: string;
    keyFeatures: string;
    platformCompatibility: string;
    pricingModel: string;
    additionalNotes: string;
    language: string;
}

export interface CustomAiStoryTemplate {
    id: string;
    name: string;
    body: string;
    target: number;
    isVisibleByDefault: boolean;
}

export interface StorymapAiTemplateOverride {
    libraryTemplateId: string;
    isVisible: boolean;
}

export const defaultViewOptions: ViewTypeOptions = {
    isShowAnnotation: false,
    isShowIssueType: false,
    isShowAssignee: false,
    isShowPersonaBlock: false,
    isShowPrioritization: false,
};

export enum ViewType {
    Discovery = 1,
    Planning = 2,
    Backlog = 3,
    Tracking = 4,
    Classic = 5,
    Timeline = 6,
}

export interface ViewTypeOptions {
    isShowPersonaBlock: boolean;
    isShowAnnotation: boolean;
    isShowAssignee: boolean;
    isShowPrioritization: boolean;
    isShowIssueType: boolean;
}

export interface CardDetailsDialogState {
    callbackAfterClose: () => void;
}

export interface PersonaManagementPopupState {
    isOpen: boolean;
    startGeneratingWithAi: boolean;
    onAiDialogClose: () => void;
    onPersonaManagementClose: () => void;
}

export interface CollectUserGoalsDialogState {
    isOpen: boolean;
    activityOrder: number | null;
    scope: string;
}

export interface AiDataDialogState {
    isAiDataDialogOpen: boolean;
    onDialogClose: () => void;
}

export interface Annotation {
    id: string;
    icon?: AnnotationIconType;
    emoji: string;
    isEmoji: boolean;
    color: AnnotationIconColorType;
    name: string;
    isArchived: boolean;
    order: number;
}

export interface Persona {
    id: string;
    name: string;
    description: string;
    group: string;
    isGlobal: boolean;
    picture: string;
    isNew: boolean;

    customFields: PersonaCustomField[];
    personalities: PersonaPersonality[];
}

export interface PersonaCustomField {
    id: string;
    label: string;
    value: string;
}

export interface PersonaPersonality {
    id: string;
    startingLabel: string;
    endingLabel: string;
    value: number;
}

export interface Color {
    defaultName: string;
    name: string;
    color: number;
}

export interface Release {
    id: string;
    prefix: string;
    name: string;
    goal: string;
    order: number;
    isArchived: boolean;
    archivedBy?: string;
    archivedAt?: string;
    isPinned: boolean;
    isCollapsed: boolean;
    startDate?: string;
    releaseDate?: string;
    isFocused: boolean;
    isNew: boolean;
}

export interface SubtaskStatus {
    key: number;
    name: string;
    color: string;
    category: SubtaskStatusCategory;
    order: number;
}

export enum SubtaskStatusCategory {
    Todo = 10,
    Active = 20,
    Done = 30,
}

export interface Activity {
    id: string;
    title: string;
    description: string;
    // Set once the card's full description was loaded by card details; a later preload must not replace it (R18).
    isFullDescriptionLoaded?: boolean;
    order: number;
    color: number;
    assigneeUserId: string;
    assigneeDisplayName?: string;
    hasAssignee?: boolean;
    key: string;
    isCollapsed: boolean;

    isActive: boolean;
    isMultiselected: boolean;
    isTitleEditing: boolean;
    isNew: boolean;
    isCopied: boolean;
    isCut: boolean;

    activeUsers: string[];

    personaIds: string[];
    annotationIds: string[];

    hasDescription: boolean;
    hasAttachment: boolean;
    hasComment: boolean;
    hasFeedback: boolean;

    isLinkedToIntegrationTool: boolean;
    linkedUrl: string;
    linkedItemKey: string;
    linkedItemTypeId: string;
    linkedItemTypeName: string;
    isSaving: boolean;

    createdAt: Date;
    createdByUserId: string;
    lastUpdatedAt: Date;
    lastUpdatedByUserId: string;
    isWatched?: boolean;
}

export interface Task {
    id: string;
    title: string;
    description: string;
    // Set once the card's full description was loaded by card details; a later preload must not replace it (R18).
    isFullDescriptionLoaded?: boolean;
    order: number;
    color: number;
    assigneeUserId: string;
    assigneeDisplayName?: string;
    hasAssignee?: boolean;
    key: string;

    activityId: string;

    isActive: boolean;
    isMultiselected: boolean;
    isTitleEditing: boolean;
    isNew: boolean;
    isCopied: boolean;
    isCut: boolean;

    activeUsers: string[];

    personaIds: string[];
    annotationIds: string[];

    hasDescription: boolean;
    hasAttachment: boolean;
    hasComment: boolean;
    hasFeedback: boolean;

    isLinkedToIntegrationTool: boolean;
    linkedUrl: string;
    linkedItemKey: string;
    linkedItemTypeId: string;
    linkedItemTypeName: string;
    isSaving: boolean;

    createdAt: Date;
    createdByUserId: string;
    lastUpdatedAt: Date;
    lastUpdatedByUserId: string;
    isWatched?: boolean;
}

export interface Subtask {
    id: string;
    title: string;
    description: string;
    // Set once the card's full description was loaded by card details; a later preload must not replace it (R18).
    isFullDescriptionLoaded?: boolean;
    order: number;
    releaseViewOrder: number;
    color: number;
    estimation?: number;
    status: number;
    assigneeUserId: string;
    assigneeDisplayName?: string;
    hasAssignee?: boolean;
    key: string;

    releaseId?: string;
    activityId: string;
    taskId: string;

    isActive: boolean;
    isMultiselected: boolean;
    isTitleEditing: boolean;
    isNew: boolean;
    isCopied: boolean;
    isCut: boolean;

    activeUsers: string[];

    personaIds: string[];
    annotationIds: string[];

    basicPrioValue: BasicPrioValue;
    moscowPrioValue: MoscowPrioValue;
    kanoPrioValue: KanoPrioValue;
    businessValue: BusinessValue;
    effort: Effort;

    hasDescription: boolean;
    hasAttachment: boolean;
    hasComment: boolean;
    hasFeedback: boolean;

    isLinkedToIntegrationTool: boolean;
    linkedUrl: string;
    linkedItemKey: string;
    linkedItemTypeId: string;
    linkedItemTypeName: string;
    isSaving: boolean;

    createdAt: Date;
    createdByUserId: string;
    lastUpdatedAt: Date;
    lastUpdatedByUserId: string;
    isWatched?: boolean;
}

export enum ToolIntegrationToolType {
    NotIntegrated = 0,
    Trello = 10,
    PivotalTracker = 20,
    Jira = 30,
    GitHub = 40,
    Vsts = 50,
    Tfs = 60,
}

export enum BoardVisibility {
    Public = 0,
    WorkspaceVisible = 1,
    Private = 2,
}
